import { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Download, Loader2, TrendingUp, TrendingDown, Landmark, Receipt } from 'lucide-react';
import { toast } from 'sonner';

export const eur = (v) => `€ ${Number(v || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export default function EsquarcioOverview() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => { axiosInstance.get('/esquarcio/summary', { params: { year } }).then((r) => setS(r.data)).catch(() => {}); }, [year]);

  const download = async (quarter) => {
    setBusy(quarter || 'jaar');
    try {
      const r = await axiosInstance.get('/esquarcio/export', { params: { year, quarter: quarter || undefined }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `Esquarcio_${year}${quarter ? `_Q${quarter}` : ''}.zip`; a.click(); URL.revokeObjectURL(url);
      toast.success('Export gedownload');
    } catch { toast.error('Export mislukt'); } finally { setBusy(''); }
  };

  const years = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));
  const max = s ? Math.max(1, ...Object.values(s.months).flatMap((m) => [m.in, m.uit])) : 1;
  const stat = (label, value, Icon, cls, testid) => (
    <Card className="border-gray-200 shadow-sm"><CardContent className="p-4">
      <p className="text-xs text-gray-500 flex items-center gap-1"><Icon className={`w-3.5 h-3.5 ${cls}`} /> {label}</p>
      <p className={`text-2xl font-bold mt-1 ${cls}`} data-testid={testid}>{eur(value)}</p>
    </CardContent></Card>
  );

  return (
    <div className="space-y-5" data-testid="esq-overview">
      <div className="flex flex-wrap items-center gap-3">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="border rounded px-3 py-2 text-sm" data-testid="esq-year-select">{years.map((y) => <option key={y}>{y}</option>)}</select>
        <div className="ml-auto flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((q) => <Button key={q} size="sm" variant="outline" disabled={!!busy} onClick={() => download(q)} data-testid={`esq-export-q${q}`}>{busy === q ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Q{q}</Button>)}
          <Button size="sm" disabled={!!busy} onClick={() => download(null)} className="bg-[#0f3b57] hover:bg-[#0b2d43]" data-testid="esq-export-year">{busy === 'jaar' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Heel {year} (ZIP)</Button>
        </div>
      </div>
      {s && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stat('Omzet excl. btw', s.omzet_excl, TrendingUp, 'text-emerald-700', 'esq-omzet')}
          {stat('Kosten excl. btw', s.kosten_excl, TrendingDown, 'text-red-700', 'esq-kosten')}
          {stat('Resultaat excl. btw', s.resultaat_excl, Landmark, s.resultaat_excl >= 0 ? 'text-[#0f3b57]' : 'text-red-700', 'esq-resultaat')}
          {stat('Btw-saldo (af te dragen − voorbelasting)', s.btw_saldo, Receipt, 'text-amber-700', 'esq-btw-saldo')}
        </div>
        <Card className="border-gray-200 shadow-sm"><CardContent className="p-4">
          <div className="flex justify-between text-xs text-gray-500 mb-3"><span>Per maand — <span className="text-emerald-700 font-semibold">omzet incl.</span> / <span className="text-red-700 font-semibold">kosten incl.</span></span><span>{s.aantal_facturen} facturen · {s.aantal_documenten} inkomende documenten · open: {eur(s.open_facturen)}</span></div>
          <div className="grid grid-cols-12 gap-2 items-end h-40">
            {Object.entries(s.months).sort(([a], [b]) => a.localeCompare(b)).map(([m, v], i) => (
              <div key={m} className="flex flex-col items-center gap-1 h-full justify-end" title={`${MONTHS[i]}: in ${eur(v.in)} · uit ${eur(v.uit)}`}>
                <div className="flex items-end gap-0.5 w-full h-full justify-center">
                  <div className="w-2/5 bg-emerald-500 rounded-t" style={{ height: `${(v.in / max) * 100}%` }} />
                  <div className="w-2/5 bg-red-400 rounded-t" style={{ height: `${(v.uit / max) * 100}%` }} />
                </div>
                <span className="text-[10px] text-gray-500">{MONTHS[i]}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      </>)}
    </div>
  );
}
